import { useCallback, useEffect, useRef, useState } from 'react';

// Cache hook
export function useCache(ttlMs = 300_000) {
  // cacheRef: { [key: string]: { data: any, timestamp: number } }
  const cacheRef = useRef({});

  // key: string, value: any
  const setCache = useCallback((key, value) => {
    cacheRef.current[key] = {
      data: value,
      timestamp: Date.now()
    };
  }, []);

  // key: string
  const getCache = useCallback((key) => {
    const entry = cacheRef.current[key];
    if (!entry) return undefined;
    if (Date.now() - entry.timestamp > ttlMs) {
      delete cacheRef.current[key];
      return undefined;
    }
    return entry.data;
  }, []);

  return { setCache, getCache };
}

// Debounce hook
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Script loader
export function useExternalScript(url) {
  const [status, setStatus] = useState(url ? 'loading' : 'idle');

  useEffect(() => {
    if (!url) {
      setStatus('idle');
      return;
    }

    // Check if the script tag already exists to avoid adding it multiple times
    let script = document.querySelector(`script[src="${url}"]`);

    if (!script) {
      script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      document.body.appendChild(script);

      const setAttributeFromEvent = (event) => {
        script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
      };

      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      // If script exists, check its status
      setStatus(script.getAttribute('data-status') || 'loading');
    }

    // A callback to set the status of our hook
    const setStateFromEvent = (event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    // Cleanup listeners on component unmount
    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [url]);

  return status;
};

export function useDebounceAsync(asyncFunction, errorMessage = 'Error during async operation:') {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const activeRequestRef = useRef(0);

  const execute = useCallback(async (...args) => {
    setIsLoading(true);
    setError(null);

    const requestId = ++activeRequestRef.current;

    try {
      const response = await asyncFunction(...args);
      if (activeRequestRef.current === requestId) {
        setData(response);
      }
    } catch (e) {
      if (activeRequestRef.current === requestId) {
        setError(e);
        setData(null);
      }
      console.error(errorMessage, e);
    } finally {
      if (activeRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [asyncFunction]);

  return { execute, isLoading, data, error };
}
